import {
	PurchaseOrderReceiptDispositionStatus,
	PurchaseOrderReceiptDispositionStatusLabels,
} from '@supply-mover-2-0/shared';
import type { ListBuilderColumn } from '../types';

/**
 * Column definitions for the tenant-wide Receipts list page (listKey
 * `purchase_order_receipts_main`). Co-located with the main config in
 * a separate file to keep each file under ~200 lines per the
 * receipts-feature path discipline (D9 in PLAN.md).
 *
 * Embedded use (PurchaseOrderReceiptsTab on the PO detail page) uses a
 * smaller column set defined in `purchase_order_receipt.config.ts`.
 */

const dispositionColors: Record<
	PurchaseOrderReceiptDispositionStatus,
	{ bg: string; text: string }
> = {
	[PurchaseOrderReceiptDispositionStatus.CLEAN]: {
		bg: '#10B981',
		text: '#FFFFFF',
	},
	[PurchaseOrderReceiptDispositionStatus.HAS_OPEN_EXCEPTIONS]: {
		bg: '#F59E0B',
		text: '#FFFFFF',
	},
	[PurchaseOrderReceiptDispositionStatus.RESOLVED]: {
		bg: '#6B7280',
		text: '#FFFFFF',
	},
};

export const PURCHASE_ORDER_RECEIPTS_MAIN_COLUMNS: Array<ListBuilderColumn> = [
	{
		id: 'receipt-number',
		fields: [
			{
				id: 'receipt-number-field',
				link: {
					type: 'object',
					enabled: true,
					objectTable: 'purchase_order_receipt',
					idField: '_id',
				},
				dataConfig: {
					type: 'field',
					field: { fieldName: 'displayId' },
					defaultValue: '-',
				},
				styleConfig: {
					size: 'sm',
					colorRule: { type: 'theme', themeColor: 'primary' },
				},
				displayStyle: 'text',
			},
		],
		headers: [{ id: 'h-receipt-number', text: 'Receipt #' }],
		minWidth: 120,
		sortable: true,
		alignment: 'left',
		sortFields: [
			{
				objectTable: 'purchase_order_receipt',
				direction: 'ASC',
				fieldName: 'displayId',
			},
		],
		widthConfig: { mode: 'flexible', preset: 'medium' },
		verticalAlignment: 'middle',
	},
	{
		id: 'receipt-date',
		fields: [
			{
				id: 'receipt-date-field',
				dataConfig: {
					type: 'field',
					field: { fieldName: 'receiptDate' },
					defaultValue: '-',
				},
				styleConfig: { size: 'sm', colorRule: { type: 'default' } },
				displayStyle: 'date',
			},
		],
		headers: [{ id: 'h-receipt-date', text: 'Receipt Date' }],
		minWidth: 130,
		sortable: true,
		alignment: 'center',
		sortFields: [
			{
				objectTable: 'purchase_order_receipt',
				direction: 'DESC',
				fieldName: 'receiptDate',
			},
		],
		widthConfig: { mode: 'flexible', preset: 'medium' },
		verticalAlignment: 'middle',
	},
	{
		id: 'vendor',
		fields: [
			{
				id: 'vendor-name-field',
				dataConfig: {
					type: 'field',
					field: { fieldName: 'vendor.name' },
					defaultValue: '-',
				},
				styleConfig: { size: 'sm', colorRule: { type: 'default' } },
				displayStyle: 'text',
			},
		],
		headers: [{ id: 'h-vendor', text: 'Vendor' }],
		minWidth: 160,
		sortable: true,
		alignment: 'left',
		sortFields: [
			{
				objectTable: 'vendor',
				direction: 'ASC',
				fieldName: 'name',
			},
		],
		widthConfig: { mode: 'flexible', preset: 'large' },
		verticalAlignment: 'middle',
	},
	{
		id: 'warehouse',
		fields: [
			{
				id: 'warehouse-name-field',
				dataConfig: {
					type: 'field',
					field: { fieldName: 'warehouse.name' },
					defaultValue: '-',
				},
				styleConfig: { size: 'sm', colorRule: { type: 'default' } },
				displayStyle: 'text',
			},
		],
		headers: [{ id: 'h-warehouse', text: 'Warehouse' }],
		minWidth: 130,
		sortable: true,
		alignment: 'left',
		sortFields: [
			{
				objectTable: 'warehouse',
				direction: 'ASC',
				fieldName: 'name',
			},
		],
		widthConfig: { mode: 'flexible', preset: 'medium' },
		verticalAlignment: 'middle',
	},
	{
		id: 'packing-slip',
		fields: [
			{
				id: 'packing-slip-field',
				dataConfig: {
					type: 'field',
					field: { fieldName: 'packingSlipNumber' },
					defaultValue: '-',
				},
				styleConfig: { size: 'sm', colorRule: { type: 'default' } },
				displayStyle: 'text',
			},
		],
		headers: [{ id: 'h-packing-slip', text: 'Packing Slip' }],
		minWidth: 110,
		sortable: true,
		alignment: 'left',
		sortFields: [
			{
				objectTable: 'purchase_order_receipt',
				direction: 'ASC',
				fieldName: 'packingSlipNumber',
			},
		],
		widthConfig: { mode: 'flexible', preset: 'small' },
		verticalAlignment: 'middle',
	},
	{
		id: 'disposition-status',
		fields: [
			{
				id: 'disposition-status-field',
				dataConfig: {
					type: 'field',
					// PD-14563: display the server-provided human label —
					// rendering the raw dispositionStatus leaked enum
					// constants (HAS_OPEN_EXCEPTIONS) into the badge. The
					// color rules below still key on the raw field.
					field: { fieldName: 'dispositionStatusLabel' },
					defaultValue:
						PurchaseOrderReceiptDispositionStatusLabels[
							PurchaseOrderReceiptDispositionStatus.CLEAN
						],
				},
				styleConfig: {
					size: 'sm',
					colorRule: {
						type: 'conditional',
						rules: Object.values(PurchaseOrderReceiptDispositionStatus).map(
							(status) => ({
								field: { fieldName: 'dispositionStatus' },
								operator: 'equals',
								value: status,
								color: dispositionColors[status],
							}),
						),
					},
				},
				displayStyle: 'badge',
			},
		],
		headers: [{ id: 'h-disposition-status', text: 'Status' }],
		minWidth: 130,
		sortable: true,
		alignment: 'center',
		sortFields: [
			{
				objectTable: 'purchase_order_receipt',
				direction: 'ASC',
				fieldName: 'dispositionStatus',
			},
		],
		widthConfig: { mode: 'flexible', preset: 'small' },
		verticalAlignment: 'middle',
	},
	{
		id: 'created-date',
		fields: [
			{
				id: 'created-date-field',
				dataConfig: {
					type: 'field',
					// PD-14560: the API serializes the audit column as the
					// entity property `createdDate` — `_created_date` never
					// matched a response key, so the column rendered '-'.
					field: { fieldName: 'createdDate' },
					defaultValue: '-',
				},
				styleConfig: { size: 'xs', colorRule: { type: 'default' } },
				displayStyle: 'date',
			},
		],
		headers: [{ id: 'h-created-date', text: 'Created' }],
		minWidth: 110,
		sortable: true,
		alignment: 'center',
		sortFields: [
			{
				objectTable: 'purchase_order_receipt',
				direction: 'DESC',
				fieldName: 'createdDate',
			},
		],
		widthConfig: { mode: 'flexible', preset: 'small' },
		verticalAlignment: 'middle',
	},
	{
		id: 'actions',
		fields: [
			{
				id: 'actions-dropdown',
				dataConfig: { type: 'custom_text', customText: '' },
				styleConfig: { size: 'md', colorRule: { type: 'default' } },
				displayStyle: 'actions_dropdown',
				dropdownActions: [
					{
						id: 'edit-action',
						label: 'Edit',
						iconName: 'edit',
						callbackName: 'edit',
						variant: 'primary',
						visibility: 'visible',
					},
					{
						id: 'delete-action',
						label: 'Delete',
						iconName: 'trash',
						callbackName: 'delete',
						variant: 'danger',
						visibility: 'visible',
					},
				],
			},
		],
		headers: [{ id: 'h-actions', text: '' }],
		width: 80,
		align: 'center',
		sortable: false,
	},
];

export const PURCHASE_ORDER_RECEIPTS_MAIN_REQUIRED_FIELDS: Array<string> = [
	'purchase_order_receipt._id',
	'purchase_order_receipt.externalId',
	'purchase_order_receipt.receiptDate',
	'purchase_order_receipt.dispositionStatus',
	'purchase_order_receipt.packingSlipNumber',
	'purchase_order_receipt._created_date',
	'vendor.id',
	'vendor.name',
	'warehouse.id',
	'warehouse.name',
];
